import { NumberEntry } from '../types/angelNumber';
import { slugify, section } from './compose';

type CuratedInput = Omit<NumberEntry, 'slug' | 'interpretationType'>;

/**
 * Hand-written entries for the numbers people look up most.
 * Every other entry in the library is composed from digit themes,
 * but all of them share this exact schema.
 */
const curated: CuratedInput[] = [
  {
    id: '000',
    number: '000',
    displayNumber: '000',
    type: 'angel_number',
    categories: ['angel', 'spiritual_growth', 'new_beginnings', 'inner_peace', 'reflection'],
    title: 'Infinite potential & spiritual reset',
    shortMeaning: 'Often interpreted as open space — the pause between one cycle and the next.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 000 is often interpreted as a reset: a moment where very little is fixed and almost anything could still be chosen. Symbolically, zero has no edges, so many people read it as a reminder that you are not as locked in as it can feel. It tends to be noticed during in-between times, when one chapter has closed and the next has not announced itself yet.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 000 is often read as permission to begin again from honesty rather than history. That might mean a genuinely fresh connection, or meeting someone familiar without the weight of every old assumption. You may use it as a cue to ask what you actually want from closeness now.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In work and money, this number is symbolically linked to the blank page — the stage before a plan hardens into a commitment. Rather than pushing for certainty, some traditions treat 000 as an invitation to stay curious a little longer and let the right direction become obvious.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 000 is often described as wholeness: the sense of returning to a calm centre that was never actually lost. Many people take it as a reminder that stillness is a legitimate place to be, not a delay before something more useful.',
    ),
    affirmation: { text: 'I make space for what wants to begin.' },
    reflectionPrompts: [
      'Pause and notice what you were thinking about when you saw 000.',
      'What feels genuinely open in your life right now?',
      'What would you like to begin from a clean page?',
      'What are you carrying that belongs to a chapter that has already closed?',
    ],
    keywords: ['reset', 'potential', 'wholeness', 'oneness', 'infinite', 'spiritual awakening'],
    relatedNumbers: ['111', '999', '1111', '888'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 78,
    searchAliases: ['000', '0', '00', '0-0-0'],
    metadata: { energy: 'Open', theme: 'Reset', element: 'Ether' },
  },
  {
    id: '111',
    number: '111',
    displayNumber: '111',
    type: 'angel_number',
    categories: ['angel', 'new_beginnings', 'manifestation', 'awakening', 'guidance'],
    title: 'New beginnings & manifestation',
    shortMeaning: 'Often interpreted as a fresh start, and a reminder that attention shapes what grows.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 111 is often associated with new beginnings, fresh opportunities and becoming more intentional about the direction of your life. Symbolically, the repeated one is read as a spotlight on whatever you have been thinking about most. Many people use it as a reminder that attention is a kind of investment — and to check where theirs is going.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 111 is often interpreted as the start of a new chapter: a first conversation, a decision to be more open, or a shift in how you show up for someone you already know. Some traditions read it as encouragement to say the honest thing first, rather than waiting to be read correctly.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In career and money, 111 is frequently linked to initiative — the idea worth starting before it feels fully ready. Symbolically it favours the first step over the perfect plan. You may take it as a nudge to put something into motion this week rather than next quarter.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 111 is often described as alignment between thought and intention. Some traditions frame it as a doorway; others simply as a reminder that what you rehearse internally tends to shape what you notice externally.',
    ),
    numerologyMeaning: section(
      'Numerology',
      'In numerology, one is associated with initiative and independence. Repeated three times, it is often read as that quality asking to be expressed rather than postponed.',
    ),
    affirmation: { text: 'I trust the next step and remain open to new possibilities.' },
    reflectionPrompts: [
      'Pause and notice what you were thinking about when you saw the number.',
      'Set an intention for what you want to create next.',
      'What is one small first step you could take within 24 hours?',
      'Where has your attention been going by habit rather than by choice?',
    ],
    keywords: ['new beginnings', 'manifestation', 'intention', 'start', 'angel'],
    relatedNumbers: ['1111', '222', '333', '1010', '11'],
    isPopular: true,
    isFeatured: true,
    popularityScore: 98,
    searchAliases: ['111', '1', '1-1-1', 'one one one'],
    metadata: { energy: 'High', theme: 'Awareness', element: 'Air' },
  },
  {
    id: '222',
    number: '222',
    displayNumber: '222',
    type: 'angel_number',
    categories: ['angel', 'balance', 'love', 'relationships', 'inner_peace', 'guidance'],
    title: 'Balance, harmony & trust',
    shortMeaning: 'Often read as reassurance: things are building, even quietly.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 222 is often interpreted as a signal to trust timing. Symbolically, twos are about partnership and balance, so many people notice this sequence when they are waiting on something and tempted to force it. Read gently, it suggests that steady beats urgent right now.',
    ),
    loveMeaning: section(
      'Love',
      'In relationships, 222 is frequently associated with meeting halfway — listening more closely, and letting a bond deepen at its own pace. Some traditions link it to reconciliation or renewed understanding, though always as an invitation rather than a promise.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In work, 222 is often read as collaboration and quiet progress: the results of effort that has not surfaced yet. You may use it as a prompt to strengthen one working relationship rather than to take on something new.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, this number is often described as faith in the pause — the idea that waiting can be an active, peaceful choice rather than lost time.',
    ),
    twinFlameMeaning: section(
      'Twin Flame Symbolism',
      'Some spiritual traditions connect 222 with twin-flame or soul-connection language, usually as a symbol of mirroring and balance. It is best held lightly: as a reminder to stay honest and self-respecting inside a connection, not as a prediction about a specific person.',
    ),
    affirmation: { text: 'I move at the pace of trust.' },
    reflectionPrompts: [
      'What are you trying to force that might work better with patience?',
      'Which relationship in your life would benefit from you listening first?',
      'Where could you meet someone halfway this week?',
      'What is quietly building that you have not given yourself credit for?',
    ],
    keywords: ['balance', 'harmony', 'trust', 'patience', 'partnership', 'twin flame'],
    relatedNumbers: ['2222', '111', '444', '1212', '22'],
    isPopular: true,
    isFeatured: true,
    popularityScore: 95,
    searchAliases: ['222', '2', '2-2-2', 'two two two'],
    metadata: { energy: 'Gentle', theme: 'Harmony', element: 'Water' },
  },
  {
    id: '333',
    number: '333',
    displayNumber: '333',
    type: 'angel_number',
    categories: ['angel', 'creativity', 'transformation', 'guidance', 'manifestation'],
    title: 'Growth, creativity & divine guidance',
    shortMeaning: 'Symbolically associated with expansion, expression and encouragement.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 333 is often associated with growth, creative expression and a sense of being encouraged. Symbolically, threes point toward expansion — the idea wanting to be made, the thing wanting to be said. Many people notice it when they are holding something in and would feel lighter letting it out.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 333 is often read as warmth and playfulness: humour, honesty and the courage to express affection out loud. Some traditions link it to communication that has been postponed for too long.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In career, 333 is frequently associated with creative work and self-expression — projects that feel more like yours than assigned. Read symbolically, it favours making a rough version over waiting for the perfect one.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 333 is often described as guidance arriving through inspiration rather than instruction: an idea that will not leave you alone is treated as a form of direction.',
    ),
    affirmation: { text: 'I let myself express what is mine to say.' },
    reflectionPrompts: [
      'What idea keeps returning to you, asking to be made real?',
      'What have you been holding back from saying?',
      'Where could you allow yourself to be a beginner again?',
      'What would you make this month if nobody had to see it?',
    ],
    keywords: ['growth', 'creativity', 'guidance', 'expression', 'joy'],
    relatedNumbers: ['3333', '111', '888', '1234', '33'],
    isPopular: true,
    isFeatured: true,
    popularityScore: 93,
    searchAliases: ['333', '3', '3-3-3', 'three three three'],
    metadata: { energy: 'Bright', theme: 'Expression', element: 'Air' },
  },
  {
    id: '444',
    number: '444',
    displayNumber: '444',
    type: 'angel_number',
    categories: ['angel', 'protection', 'balance', 'inner_peace', 'guidance'],
    title: 'Protection, stability & grounding',
    shortMeaning: 'Often experienced as steadying — a symbolic sense of being accompanied.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 444 is often interpreted as protection and stability. Symbolically, fours are foundations: the routines, people and structures that quietly hold a life together. Many people notice this sequence during uncertain periods and use it as a reminder that the ground is more solid than it feels.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 444 is often linked to consistency — care that shows up in small reliable ways rather than grand gestures. Some traditions read it as reassurance that a steady connection is worth more than an intense one.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In work, 444 tends to be associated with structure and patient effort: the unglamorous work that makes everything else possible. Read symbolically, it favours strengthening what exists over starting something new.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 444 is frequently described as the feeling of being supported — the sense that you are not carrying it all alone, however that arrives for you.',
    ),
    affirmation: { text: 'I am supported, and my foundations hold.' },
    reflectionPrompts: [
      'What foundation in your life deserves care and attention right now?',
      'Who supports you that you could thank this week?',
      'Which routine makes your life noticeably easier?',
      'Where are you safer than your worry suggests?',
    ],
    keywords: ['protection', 'stability', 'grounding', 'safety', 'foundation', 'angel'],
    relatedNumbers: ['4444', '222', '888', '1111', '44'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 92,
    searchAliases: ['444', '4', '4-4-4', 'four four four'],
    metadata: { energy: 'Steady', theme: 'Grounding', element: 'Earth' },
  },
  {
    id: '555',
    number: '555',
    displayNumber: '555',
    type: 'angel_number',
    categories: ['angel', 'transformation', 'new_beginnings', 'awakening', 'guidance'],
    title: 'Change, transformation & freedom',
    shortMeaning: 'Often noticed just before something shifts — symbolically linked to movement.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 555 is often associated with change — the restless, slightly electric feeling that arrives before a shift becomes visible. Symbolically, fives are movement and freedom. Many people read it as a reminder that change is usually a process they have already begun, not an event that happens to them.',
    ),
    loveMeaning: section(
      'Love',
      'In relationships, 555 is often interpreted as honesty about what needs to change so the connection can breathe. Some traditions link it to a new phase rather than an ending — though it can also mark the courage to let go.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In career, 555 is frequently read as a turning point: a role, place or rhythm that no longer fits the way it used to. Symbolically it favours exploring options openly instead of deciding under pressure.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 555 is often described as trusting transformation — treating change as something you can move with rather than brace against.',
    ),
    affirmation: { text: 'I move with change instead of against it.' },
    reflectionPrompts: [
      'What change have you been quietly preparing for?',
      'What would you choose if the decision felt safe?',
      'What no longer fits the person you are becoming?',
      'Where could you allow more freedom in your week?',
    ],
    keywords: ['change', 'transformation', 'freedom', 'movement', 'adventure'],
    relatedNumbers: ['5555', '999', '111', '1212', '55'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 91,
    searchAliases: ['555', '5', '5-5-5', 'five five five'],
    metadata: { energy: 'Dynamic', theme: 'Change', element: 'Air' },
  },
  {
    id: '666',
    number: '666',
    displayNumber: '666',
    type: 'angel_number',
    categories: ['angel', 'inner_peace', 'love', 'balance', 'relationships', 'guidance'],
    title: 'Rebalancing thoughts & returning to harmony',
    shortMeaning: 'In angel-number traditions, usually read as a gentle request to rebalance.',
    coreMeaning: section(
      'Core Meaning',
      'In angel-number traditions, 666 is rarely treated as ominous. It is far more often interpreted as a rebalancing signal: a suggestion that worry has been taking up more room than it needs to. Symbolically, sixes point toward home, care and perspective — a nudge to bring your attention back to what is actually in front of you.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 666 is often associated with nurture and the relationships closest to you. Some traditions read it as a prompt to tend to home life, or to stop keeping score in a bond that would relax if you did.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In work and money, 666 is frequently linked to the balance between effort and rest. Read symbolically, it suggests replacing financial anxiety with one practical, calming action.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 666 is often described as a return to what is loving and simple: choosing the kinder interpretation, especially of yourself.',
    ),
    historicalSymbolism: section(
      'Historical Symbolism',
      'Culturally, 666 carries associations from religious texts that are quite separate from modern angel-number practice. Contemporary spiritual writing generally reframes it around harmony and care rather than fear — worth knowing if the number unsettles you.',
    ),
    affirmation: { text: 'I return to what is loving and simple.' },
    reflectionPrompts: [
      'Where is your energy going, and where would you rather it went?',
      'What worry could you set down for the rest of today?',
      'What would rest look like if you took it seriously?',
      'Which kinder interpretation of a recent event might also be true?',
    ],
    keywords: ['harmony', 'rebalance', 'home', 'care', 'worry', 'family'],
    relatedNumbers: ['6666', '444', '222', '888', '66'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 85,
    searchAliases: ['666', '6', '6-6-6', 'six six six'],
    metadata: { energy: 'Gentle', theme: 'Care', element: 'Water' },
  },
  {
    id: '777',
    number: '777',
    displayNumber: '777',
    type: 'angel_number',
    categories: ['angel', 'intuition', 'spiritual_growth', 'awakening', 'inner_peace', 'guidance'],
    title: 'Inner wisdom & spiritual growth',
    shortMeaning: 'Often noticed when something you already sensed becomes clear.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 777 is often associated with inner wisdom — the quiet kind of knowing that arrives without argument. Symbolically, sevens are linked to study, insight and inner life. Many people notice this sequence when they are looking for outside validation for something they already believe.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 777 is often read as depth over performance: a connection where you feel genuinely known rather than impressive. Some traditions link it to trusting your own read on a relationship.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In career, 777 is frequently associated with mastery and curiosity — following the work that keeps your mind alive, even if it looks like a detour. Symbolically it favours learning over hustling.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 777 is often described as reassurance that you are on your own path, even without external proof. You may treat it as a reminder to keep listening inward.',
    ),
    affirmation: { text: 'I trust what I know quietly.' },
    reflectionPrompts: [
      'What do you already know, somewhere underneath the noise?',
      'Whose approval are you waiting for that you could give yourself?',
      'What are you curious about that has no practical purpose?',
      'When did you last trust your instinct and get it right?',
    ],
    keywords: ['wisdom', 'intuition', 'spiritual awakening', 'insight', 'luck'],
    relatedNumbers: ['7777', '333', '999', '1111', '77'],
    isPopular: true,
    isFeatured: true,
    popularityScore: 94,
    searchAliases: ['777', '7', '7-7-7', 'seven seven seven'],
    metadata: { energy: 'Quiet', theme: 'Insight', element: 'Ether' },
  },
  {
    id: '888',
    number: '888',
    displayNumber: '888',
    type: 'angel_number',
    categories: ['angel', 'abundance', 'money', 'career', 'manifestation', 'guidance'],
    title: 'Abundance, achievement & energetic balance',
    shortMeaning: 'Symbolically linked to flow — what comes in, and what goes out.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 888 is often associated with abundance and momentum. Symbolically, the eight is a continuous loop, so many traditions read it as reciprocity: the balance between giving and receiving. It is frequently noticed by people who are generous with everyone except themselves.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 888 is often interpreted as a bond where care moves in both directions. Some traditions read it as a prompt to let yourself be looked after, rather than only being the dependable one.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In career and money, 888 is frequently linked to recognition and steady momentum — effort that begins to compound. Read symbolically, it suggests noticing what is already working and doing more of that. It is a reflection prompt, not a financial forecast.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 888 is often described as the reminder that receiving is not taking, and that flow moves in cycles rather than straight lines.',
    ),
    affirmation: { text: 'I can give generously and still receive.' },
    reflectionPrompts: [
      'What are you giving generously that you find hard to accept in return?',
      'What is already working that deserves more of your energy?',
      'Where could you ask for support instead of absorbing it alone?',
      'What does "enough" actually look like for you?',
    ],
    keywords: ['abundance', 'money', 'success', 'prosperity', 'flow', 'career'],
    relatedNumbers: ['8888', '444', '111', '1010', '88'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 90,
    searchAliases: ['888', '8', '8-8-8', 'eight eight eight'],
    metadata: { energy: 'Strong', theme: 'Abundance', element: 'Earth' },
  },
  {
    id: '999',
    number: '999',
    displayNumber: '999',
    type: 'angel_number',
    categories: ['angel', 'completion', 'transformation', 'spiritual_growth', 'guidance'],
    title: 'Completion, release & transition',
    shortMeaning: 'Often noticed at the end of a chapter, before the next one is named.',
    coreMeaning: section(
      'Core Meaning',
      'Seeing 999 is often associated with completion. Symbolically, nine sits at the end of a cycle, so this sequence is frequently read as closure — something has run its course, even if it has not been said out loud. Many people find it arrives with relief rather than loss.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 999 is often interpreted as letting go of an old pattern so that love can meet who you are now. Some traditions read it as forgiveness — of a person, or of an earlier version of yourself.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In career, 999 tends to be linked to a chapter finishing and the quieter question of what your work is for. Symbolically it favours completing things well over starting something new immediately.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 999 is often described as perspective and compassion: the wisdom that comes from having lived a full cycle and being willing to close it gently.',
    ),
    affirmation: { text: 'I release what is complete with gratitude.' },
    reflectionPrompts: [
      'What feels finished, even if you have not said so out loud yet?',
      'What would a kind ending look like here?',
      'What have you outgrown that you still maintain out of habit?',
      'What do you want to carry forward, and what stays behind?',
    ],
    keywords: ['completion', 'release', 'endings', 'letting go', 'closure', 'transition'],
    relatedNumbers: ['9999', '000', '555', '111', '99'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 88,
    searchAliases: ['999', '9', '9-9-9', 'nine nine nine'],
    metadata: { energy: 'Deep', theme: 'Completion', element: 'Water' },
  },
  {
    id: '1111',
    number: '1111',
    displayNumber: '11:11',
    type: 'portal_number',
    categories: [
      'portal',
      'angel',
      'mirror',
      'time',
      'manifestation',
      'new_beginnings',
      'spiritual_growth',
      'awakening',
    ],
    title: 'Portal of Awareness',
    shortMeaning:
      'A symbolic reminder to pause, become aware of your thoughts and set an intention.',
    coreMeaning: section(
      'Core Meaning',
      'Some spiritual traditions describe 11:11 as a portal: not a literal opening, but a moment where awareness sharpens. Symbolically, four ones concentrate the energy of new beginnings and intention into a single minute. Many people notice it during periods of change, and use it as a cue to name what they actually want next.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 11:11 is often interpreted as alignment — a connection arriving at the right time, or a shift in how you show up in one you already have. It is frequently associated with twin-flame language, though it is best held lightly: as a reminder to be honest, not a prediction about a person.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In career and abundance, 11:11 is often read as a threshold: the moment before a decision becomes real. Symbolically it favours clarity over speed — knowing what you are aiming at before you accelerate.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 11:11 is often described as heightened awareness: the sense that you have briefly stepped out of autopilot. Some traditions treat it as symbolic rather than predictive — the meaning comes from the attention you bring to it.',
    ),
    portalMeaning: section(
      'Portal Symbolism',
      '“Portal” here is a symbolic idea, not a supernatural claim. Many people treat 11:11 as a one-minute threshold: a moment to set an intention, acknowledge a chapter turning, or notice how far they have come. Nothing opens or closes except your own attention — which is arguably the point.',
    ),
    twinFlameMeaning: section(
      'Twin Flame Symbolism',
      'Some spiritual traditions connect 11:11 with twin-flame or soul-recognition themes, usually as a symbol of mirroring rather than destiny. You may use it as a prompt to ask whether a connection helps you feel more like yourself.',
    ),
    numerologyMeaning: section(
      'Numerology',
      'Numerologically, eleven is treated as a master number associated with intuition and awareness. Doubled, it is often read as that theme repeating for emphasis.',
    ),
    affirmation: { text: 'I pause, notice, and choose my next intention freely.' },
    reflectionPrompts: [
      'What were you thinking about when you noticed 11:11?',
      'What intention would you like to set?',
      'What chapter of your life feels ready to change?',
      'If this were a doorway, what would you like to be walking toward?',
    ],
    keywords: [
      'portal',
      'awakening',
      'alignment',
      'synchronicity',
      'twin flame',
      'manifestation',
      'awareness',
    ],
    relatedNumbers: ['111', '2222', '222', '1212', '1234'],
    isPopular: true,
    isFeatured: true,
    popularityScore: 100,
    searchAliases: ['1111', '11:11', '11 11', '1-1-1-1', 'eleven eleven'],
    metadata: { energy: 'High', theme: 'Awareness', element: 'Air' },
  },
  {
    id: '1010',
    number: '1010',
    displayNumber: '10:10',
    type: 'special_sequence',
    categories: ['angel', 'mirror', 'time', 'new_beginnings', 'manifestation', 'spiritual_growth'],
    title: 'Beginnings & Open Space',
    shortMeaning: 'Often read as a fresh start with room to move.',
    coreMeaning: section(
      'Core Meaning',
      'Symbolically, 1010 pairs the one — beginnings, intention — with the zero, which represents open space and potential. Many people interpret it as a start that is not yet defined, which can feel either exciting or unsettling depending on the day. It is often noticed when a decision is close but not forced.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 1010 is often interpreted as a chance to begin again without the weight of every previous version of the story. Some traditions read it as clarity: knowing what you want before asking for it.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In work, 1010 is frequently linked to a new direction that is still taking shape. Read symbolically, it suggests exploring rather than committing prematurely.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 1010 is often described as alignment with your own timing — the reminder that being between chapters is a legitimate place to be.',
    ),
    affirmation: { text: 'I make space for what wants to begin.' },
    reflectionPrompts: [
      'What feels like it is beginning, even if it has no name yet?',
      'What would you like to leave undecided a little longer?',
      'What is one intention worth writing down today?',
      'Where do you need more space rather than more effort?',
    ],
    keywords: ['new beginnings', 'potential', 'alignment', 'intention'],
    relatedNumbers: ['111', '1111', '000', '1212'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 80,
    searchAliases: ['1010', '10:10', '10 10', 'ten ten'],
    metadata: { energy: 'Open', theme: 'Reset', element: 'Ether' },
  },
  {
    id: '1212',
    number: '1212',
    displayNumber: '12:12',
    type: 'special_sequence',
    categories: ['angel', 'mirror', 'time', 'new_beginnings', 'balance', 'inner_peace'],
    title: 'Growth Through Trust',
    shortMeaning: 'Often read as forward movement that does not need to be rushed.',
    coreMeaning: section(
      'Core Meaning',
      'Symbolically, 1212 alternates beginnings with balance, which is why it is often interpreted as growth that unfolds through trust rather than force. Many people notice it while stepping outside a comfort zone and looking for reassurance that the pace is acceptable.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 1212 is often associated with a relationship that is genuinely developing — steadily, and with both people contributing. Some traditions read it as a prompt to keep communicating during the awkward middle part.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In career, 1212 is frequently linked to a new phase that requires patience: momentum building underneath something that still looks quiet from the outside.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 1212 is often described as faith in your own progress — the sense that growth is happening even when it cannot be measured yet.',
    ),
    affirmation: { text: 'I move at the pace of trust.' },
    reflectionPrompts: [
      'Where are you making progress that is hard to measure?',
      'What would you do differently if you trusted the timing?',
      'What step feels slightly outside your comfort zone?',
      'Who could you be more open with this week?',
    ],
    keywords: ['growth', 'trust', 'progress', 'faith', 'alignment'],
    relatedNumbers: ['111', '222', '1111', '1234'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 82,
    searchAliases: ['1212', '12:12', '12 12', 'twelve twelve'],
    metadata: { energy: 'High', theme: 'Awareness', element: 'Air' },
  },
  {
    id: '1234',
    number: '1234',
    displayNumber: '1234',
    type: 'sequential_number',
    categories: ['sequence', 'angel', 'new_beginnings', 'inner_peace', 'guidance'],
    title: 'One Step at a Time',
    shortMeaning: 'Symbolically associated with simple, ordered progress.',
    coreMeaning: section(
      'Core Meaning',
      'Symbolically, 1234 climbs in order: beginnings, balance, growth, then stability. Many people interpret it as a reminder that you do not need the whole staircase — only the next step. It is frequently noticed by people who are impatient for an outcome that is still being built.',
    ),
    loveMeaning: section(
      'Love',
      'In love, 1234 is often associated with a connection developing in a natural order, without shortcuts. Some traditions read it as encouragement to let things be simple.',
    ),
    careerMeaning: section(
      'Career & Money',
      'In work, 1234 is frequently linked to method: breaking something overwhelming into an order you can actually follow. Read symbolically, it favours the next task over the full plan.',
    ),
    spiritualMeaning: section(
      'Spiritual Meaning',
      'Spiritually, 1234 is often described as trusting a gradual path — progress that is quiet, sequential and cumulative.',
    ),
    affirmation: { text: 'I take the next step and let the rest arrive.' },
    reflectionPrompts: [
      'What is the very next step — not the whole plan, just the next one?',
      'What are you overcomplicating?',
      'What order would make this feel manageable?',
      'What progress have you already made that you skipped past?',
    ],
    keywords: ['progress', 'steps', 'order', 'growth', 'sequence'],
    relatedNumbers: ['111', '222', '333', '1111', '2345'],
    isPopular: true,
    isFeatured: false,
    popularityScore: 79,
    searchAliases: ['1234', '12:34', '1-2-3-4', 'one two three four'],
    metadata: { energy: 'Steady', theme: 'Grounding', element: 'Earth' },
  },
];

export const curatedNumbers: NumberEntry[] = curated.map((entry) => ({
  ...entry,
  slug: slugify(entry.displayNumber, entry.title),
  interpretationType: 'symbolic_spiritual',
}));
